'use client';

import React, { useCallback, useState, useMemo, useEffect, useRef } from 'react';
import { advancedSearchClient } from 'components/essentials/AlgoliaSearchClient';
import { InstantSearch, Configure } from 'react-instantsearch';
import CatchAlgoliaSearchError from 'components/essentials/CatchAlgoliaSearchError';
import classNames from 'classnames';
import { ALGOLIA_INDEX_NAME, NUMBER_OF_PRODUCTS_TO_SHOW } from 'commons/constants';
import scrollToTop from 'commons/scrollToTop';
import { BASE_ATTRIBUTES_TO_RETRIEVE, appendInStockWarehouseAttributeIfZip } from 'components/consumer/category-page-algolia/categoryPageTypes';
import useIsBrowser from 'hooks/useIsBrowser';
import useScreen from 'hooks/useScreen';
import useWarehouseIdForUser from 'global-state/warehouseIdForUser/useWarehouseIdForUser';
import useZipCodeFromUser from 'global-state/zipCodeFromUser/useZipCodeFromUser';
import { useTransition } from './hooks/reactShims';
import AlgoliaSearchBox from './components/AlgoliaSearchBox';
import AlgoliaHits from './components/AlgoliaHits';
import AdvancedSearchContext from './hooks/AdvancedSearchContext';
import useAdvancedSearchURL from './hooks/useAdvancedSearchURL';
import MobileSearchIndexSelector from './components/MobileSearchIndexSelector';
import buildSearchAlgoliaConfigureFilters from './buildSearchAlgoliaConfigureFilters';
const AdvancedSearchNext = () => {
  const {
    pathname,
    queryUrlParam,
    categoryUrlParam,
    activeTypeFilter,
    pagesUrlParam,
    updateUrlParamImmediate
  } = useAdvancedSearchURL();
  const warehouseId = useWarehouseIdForUser();
  const hasUserZip = !!useZipCodeFromUser();
  const isBrowser = useIsBrowser();
  const {
    isMobileOrTablet
  } = useScreen();

  // Ref keeps the latest updateUrlParamImmediate without adding it as a dep
  // to callbacks that must remain stable (e.g. onQueryChanging).
  const updateUrlParamImmediateRef = useRef(updateUrlParamImmediate);
  updateUrlParamImmediateRef.current = updateUrlParamImmediate;
  const pendingQueryRef = useRef(null);
  const [remountCounter, setRemountCounter] = useState(0);
  const previousQueryRef = useRef(queryUrlParam);
  const hasMountedRef = useRef(false);
  // Hydration = useSearchParams catching up to window.location q at mount only.
  const initialLocationQueryRef = useRef(typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('q') ?? '' : '');
  const hydrationCatchUpDoneRef = useRef(Boolean(initialLocationQueryRef.current) && Boolean(queryUrlParam));
  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      previousQueryRef.current = queryUrlParam;
      return;
    }
    const queryChanged = queryUrlParam !== previousQueryRef.current;
    const queryMatchesPending = queryUrlParam === pendingQueryRef.current;
    const wasHydrationTransitionFromEmpty = !hydrationCatchUpDoneRef.current && previousQueryRef.current === '' && Boolean(queryUrlParam) && queryUrlParam === initialLocationQueryRef.current;
    const wasExternalNavigation = queryChanged && !queryMatchesPending && queryUrlParam && !wasHydrationTransitionFromEmpty;
    if (wasHydrationTransitionFromEmpty) {
      hydrationCatchUpDoneRef.current = true;
    }
    if (wasExternalNavigation) {
      setRemountCounter(c => c + 1);
    }
    if (queryMatchesPending) {
      pendingQueryRef.current = null;
    }
    previousQueryRef.current = queryUrlParam;
  }, [queryUrlParam]);
  const instantSearchKey = useMemo(() => `${pathname}-${remountCounter}`, [pathname, remountCounter]);
  const [currentProductIndex, setCurrentProductIndex] = useState(ALGOLIA_INDEX_NAME.PRODUCTS);
  // Category tab is owned by the URL (`?category=`). Soft-nav from search modal
  // "View results by" updates the query string on the same /search pathname; if
  // this lived only in useState it would stick on the previous tab.
  const currentSearchSection = categoryUrlParam ?? ALGOLIA_INDEX_NAME.PRODUCTS;
  const [isReadyToShipActive, setIsReadyToShipActive] = useState(false);
  const [minWidth, setMinWidth] = useState('');
  const [maxWidth, setMaxWidth] = useState('');
  const [selectedColors, setSelectedColors] = useState({});

  // typeFilter is stored in sessionStorage (keyed by pathname) rather than the URL.
  // This matches the approach for RTS/Width/Colors: React state preserved by the router
  // cache and restored from sessionStorage on full remounts (e.g. after cache miss).
  const [typeFilter, setTypeFilter] = useState(() => {
    if (typeof window === 'undefined') return activeTypeFilter || null;
    const sessionKey = `plp-type:${window.location.pathname}`;
    return sessionStorage.getItem(sessionKey) ?? activeTypeFilter ?? null;
  });
  const updateTypeFilter = useCallback(value => {
    if (typeof window !== 'undefined') {
      const sessionKey_0 = `plp-type:${window.location.pathname}`;
      if (value) {
        sessionStorage.setItem(sessionKey_0, value);
      } else {
        sessionStorage.removeItem(sessionKey_0);
      }
    }
    setTypeFilter(value ?? null);
  }, []);
  const updateTypeFilterRef = useRef(updateTypeFilter);
  updateTypeFilterRef.current = updateTypeFilter;
  const onQueryChanging = useCallback(newQuery => {
    pendingQueryRef.current = newQuery;
    setIsReadyToShipActive(false);
    setMinWidth('');
    setMaxWidth('');
    setSelectedColors({});
    updateUrlParamImmediateRef.current({
      pages: undefined,
      type: undefined
    });
    updateTypeFilterRef.current(null);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [, startTransition] = useTransition();
  const updateSearchSection = useCallback(indexName => {
    startTransition(() => {
      // Immediate so the URL-driven `currentSearchSection` updates without lag
      updateUrlParamImmediate({
        category: indexName,
        type: undefined,
        pages: undefined
      });
      updateTypeFilterRef.current(null);
    });
    scrollToTop();
  }, [updateUrlParamImmediate]);
  const updateCurrentProductIndex = useCallback(indexName_0 => {
    startTransition(() => {
      setCurrentProductIndex(indexName_0);
    });
  }, [setCurrentProductIndex]);
  const handleToggleReadyToShip = useCallback(checked => {
    setIsReadyToShipActive(checked);
  }, []);
  const handleWidthChange = useCallback((min, max) => {
    setMinWidth(min);
    setMaxWidth(max);
  }, []);
  const selectColor = useCallback(colorLabel => {
    const isSelected = !!selectedColors?.[colorLabel];
    const newSelectedColors = {
      ...selectedColors,
      [colorLabel]: !isSelected
    };
    setSelectedColors(newSelectedColors);
  }, [selectedColors]);
  const clearSelectedColors = useCallback(() => {
    setSelectedColors({});
  }, []);
  const previousRemountCounterRef = useRef(0);
  useEffect(() => {
    if (remountCounter === 0 || remountCounter === previousRemountCounterRef.current) return;
    previousRemountCounterRef.current = remountCounter;
    setIsReadyToShipActive(false);
    setMinWidth('');
    setMaxWidth('');
    setSelectedColors({});
    updateUrlParamImmediateRef.current({
      pages: undefined,
      type: undefined
    });
    updateTypeFilterRef.current(null);
  }, [remountCounter]); // eslint-disable-line react-hooks/exhaustive-deps

  const context = useMemo(() => ({
    currentSearchSection,
    currentProductIndex,
    updateSearchSection,
    updateCurrentProductIndex,
    isReadyToShipActive,
    handleToggleReadyToShip,
    minWidth,
    maxWidth,
    handleWidthChange,
    selectedColors,
    selectColor,
    clearSelectedColors,
    activeTypeFilter: typeFilter,
    updateTypeFilter,
    pagesUrlParam,
    updateUrlParamImmediate
  }), [currentSearchSection, currentProductIndex, updateSearchSection, updateCurrentProductIndex, isReadyToShipActive, handleToggleReadyToShip, minWidth, maxWidth, handleWidthChange, selectedColors, selectColor, clearSelectedColors, typeFilter, updateTypeFilter, pagesUrlParam, updateUrlParamImmediate]);
  const initialQuery = useMemo(() => {
    if (queryUrlParam) {
      return queryUrlParam;
    }
    if (typeof window !== 'undefined') {
      return new URLSearchParams(window.location.search).get('q') ?? '';
    }
    return '';
  }, [queryUrlParam]);
  const initialUiState = useMemo(() => ({
    [ALGOLIA_INDEX_NAME.PRODUCTS]: {
      query: initialQuery,
      ...(typeFilter ? {
        refinementList: {
          type: [typeFilter]
        }
      } : {})
    },
    [ALGOLIA_INDEX_NAME.CUSTOMER_PHOTOS]: {
      query: initialQuery
    },
    [ALGOLIA_INDEX_NAME.REVIEWS]: {
      query: initialQuery
    },
    [ALGOLIA_INDEX_NAME.STATIC_PAGES]: {
      query: initialQuery
    }
  }), [initialQuery, typeFilter]);
  const configureFilters = useMemo(() => buildSearchAlgoliaConfigureFilters({
    isReadyToShipActive,
    warehouseId,
    hasUserZip
  }), [isReadyToShipActive, warehouseId, hasUserZip]);
  const productAttributesToRetrieve = useMemo(() => appendInStockWarehouseAttributeIfZip(BASE_ATTRIBUTES_TO_RETRIEVE, hasUserZip, isReadyToShipActive), [hasUserZip, isReadyToShipActive]);

  // Align with Alexandria getProductsStaticData(showSearchProductsOnly): visibility 2 or 3, item_availability 0
  const numericFilters = useMemo(() => {
    const filters = ['item_availability=0', 'visibility>1', 'visibility<4'];
    const minNum = parseInt(minWidth, 10);
    const maxNum = parseInt(maxWidth, 10);
    if (!Number.isNaN(minNum) && minNum >= 1) {
      filters.push(`item_width>=${minNum}`);
    }
    if (!Number.isNaN(maxNum) && maxNum <= 300) {
      filters.push(`item_width<=${maxNum}`);
    }
    return filters;
  }, [minWidth, maxWidth]);
  return <AdvancedSearchContext.Provider value={context}>
      <div className={classNames('w-full mt-0 lg:mt-', '[&_.ais-SearchBox-input]:pl-[30px] [&_.ais-SearchBox-input]:indent:[unset] [&_.ais-SearchBox-input]:text-left', '[&_.ais-SearchBox-input::placeholder]:text-left [&_.ais-SearchBox-input::placeholder]:indent:[unset]')}>
        <InstantSearch key={instantSearchKey} searchClient={advancedSearchClient} indexName={ALGOLIA_INDEX_NAME.PRODUCTS} initialUiState={initialUiState} future={{
        preserveSharedStateOnUnmount: false
      }}>
          <CatchAlgoliaSearchError />
          <div className="relative h-auto lg:z-[1]">
            <AlgoliaSearchBox onQueryChanging={onQueryChanging} />
          </div>

          <div className="mt-0 mx-0 mb-0 lg:mb-8 lg:flex">
            {isBrowser && isMobileOrTablet && <MobileSearchIndexSelector />}
          </div>

          <Configure facets={['*']} filters={configureFilters} numericFilters={numericFilters} hitsPerPage={NUMBER_OF_PRODUCTS_TO_SHOW} attributesToRetrieve={productAttributesToRetrieve} clickAnalytics />

          <AlgoliaHits />
        </InstantSearch>
      </div>
    </AdvancedSearchContext.Provider>;
};
export default AdvancedSearchNext;